import { useMemo } from "react";
import {
  Dot,
  Line,
  LineChart,
  ResponsiveContainer,
  ResponsiveContainerProps,
  XAxis,
  YAxis,
} from "recharts";
import { Temporal } from "temporal-polyfill";
import { type Merge } from "type-fest";

export type TimelineThumbnailProperties = Merge<
  ResponsiveContainerProps,
  {
    children?: never;
    color?: string;
    start?: Temporal.PlainDateTime;
    end?: Temporal.PlainDateTime;
    height?: number;
    max?: number;
    min?: number;
    width?: number;
    values: ReadonlyArray<[Temporal.PlainDateTime, number]>;
  }
>;

export function TimelineThumbnail({
  className,
  color,
  values,
  start,
  height,
  width,
  end,
  max,
  min,
  ...properties
}: TimelineThumbnailProperties) {
  const data = useMemo(
    function () {
      return values
        .map(function ([dateTime, data]) {
          return {
            x: Math.round(dateTime.toZonedDateTime("Asia/Tokyo").epochMilliseconds),
            y: data,
          };
        })
        .toSorted(function ({ x: a }, { x: b }) {
          return a - b;
        });
    },
    [values],
  );

  const domains = useMemo(
    function () {
      return {
        x:
          start && end
            ? ([
                Math.round(start.toZonedDateTime("Asia/Tokyo").epochMilliseconds),
                Math.round(end.toZonedDateTime("Asia/Tokyo").epochMilliseconds),
              ] as const)
            : values.length > 0
              ? ([
                  Math.round(values[0][0].toZonedDateTime("Asia/Tokyo").epochMilliseconds),
                  Math.round(
                    values[values.length - 1][0].toZonedDateTime("Asia/Tokyo").epochMilliseconds,
                  ),
                ] as const)
              : undefined,
        y:
          min !== undefined && max !== undefined
            ? ([min, max] as const)
            : values.length > 0
              ? ([
                  Math.min(...values.map(([, d]) => d)),
                  Math.max(...values.map(([, d]) => d)),
                ] as const)
              : undefined,
      };
    },
    [end, max, min, start, values],
  );

  const c = (
    <LineChart
      data={data}
      height={typeof height === "number" ? height : undefined}
      width={typeof width === "number" ? width : undefined}
    >
      <XAxis
        axisLine={false}
        dataKey="x"
        domain={domains.x}
        height={0}
        tick={false}
        tickLine={false}
        type="number"
      />
      <YAxis
        axisLine={false}
        dataKey="y"
        domain={domains.y}
        tick={false}
        tickLine={false}
        type="number"
        width={0}
      />

      <Line
        activeDot={false}
        dataKey="y"
        dot={<CustomDot color={color} data={data} />}
        stroke="#CBD5E1"
        strokeWidth={2}
      />
    </LineChart>
  );

  if (typeof height === "number" && typeof width === "number") {
    return c;
  } else {
    return (
      <ResponsiveContainer height={height} width={width} {...properties} className={className}>
        {c}
      </ResponsiveContainer>
    );
  }
}

function CustomDot({ color, data, ...props }: any) {
  const { index } = props;
  return <Dot {...props} stroke={index === data.length - 1 ? color : "#CBD5E1"} strokeWidth={2} />;
}
